import { useState } from "react";
import { Copy, Pencil, Plus } from "lucide-react";
import type { TechCalcVariant } from "../types";

const guardSwitch = (dirty: boolean) =>
  !dirty ||
  window.confirm("Masz niezapisane zmiany w rysunku. Przełączyć wariant i porzucić zmiany?");

/**
 * Wybór wariantu obliczeń (wzorem ZEVariantSwitcher). Warianty są w pełni
 * niezależne — przełączenie ładuje rysunek i wyniki wybranego wariantu.
 * Aktywny wariant można przemianować lub zduplikować (dokładna kopia
 * ZAPISANEGO rysunku + wyników). Kontrakt API nie przewiduje usuwania
 * wariantów, więc nie ma tu kosza.
 */
export function TechCalcVariantSwitcher({
  variants,
  activeVariantId,
  dirty,
  onSelect,
  onCreate,
  onRename,
  onDuplicate,
  createPending,
  duplicatePending,
}: {
  variants: TechCalcVariant[];
  activeVariantId: string | null;
  dirty: boolean;
  onSelect: (id: string) => void;
  onCreate: (name: string) => void;
  onRename: (id: string, name: string) => void;
  onDuplicate: (id: string, name: string) => void;
  createPending?: boolean;
  duplicatePending?: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");

  const nextName = () => {
    const used = new Set(variants.map((v) => v.name.toUpperCase()));
    for (let c = 65; c <= 90; c++) {
      const candidate = `Wariant ${String.fromCharCode(c)}`;
      if (!used.has(candidate.toUpperCase())) return candidate;
    }
    return `Wariant ${variants.length + 1}`;
  };

  const submit = () => {
    const n = (name || nextName()).trim();
    if (!n) return;
    onCreate(n);
    setName("");
    setAdding(false);
  };

  const active = variants.find((v) => v.id === activeVariantId);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex border border-[var(--color-border)]">
        {variants.length === 0 && (
          <span className="px-3 py-1.5 font-mono text-[11px] text-[var(--color-text-muted)]">
            — brak wariantów —
          </span>
        )}
        {variants.map((v) => {
          const isActive = v.id === activeVariantId;
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => isActive || (guardSwitch(dirty) && onSelect(v.id))}
              className={`border-r border-[var(--color-border)] px-3 py-1.5 font-mono text-sm font-bold transition-colors last:border-r-0 ${
                isActive
                  ? "bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
                  : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
              }`}
            >
              {v.name}
            </button>
          );
        })}
      </div>

      {adding ? (
        <div className="flex items-center gap-1">
          <input
            autoFocus
            className="input-base w-40 py-1 text-xs"
            placeholder={nextName()}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
          <button type="button" className="font-mono text-[11px] text-[var(--color-primary)]" onClick={submit}>
            dodaj
          </button>
          <button
            type="button"
            className="font-mono text-[11px] text-[var(--color-text-muted)]"
            onClick={() => setAdding(false)}
          >
            anuluj
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={createPending}
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1 border border-dashed border-[var(--color-border)] px-2 py-1.5 font-mono text-[11px] text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-elevated)] disabled:opacity-50"
        >
          <Plus size={13} /> Nowy wariant
        </button>
      )}

      {active && (
        <>
          <button
            type="button"
            title="Zmień nazwę aktywnego wariantu"
            onClick={() => {
              const n = window.prompt("Nowa nazwa wariantu:", active.name);
              if (n?.trim() && n.trim() !== active.name) onRename(active.id, n.trim());
            }}
            className="p-1 text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-primary)]"
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            disabled={duplicatePending}
            title="Utwórz nowy wariant jako dokładną kopię aktywnego (z OSTATNIO ZAPISANEGO rysunku)"
            onClick={() => {
              if (
                dirty &&
                !window.confirm(
                  "Masz niezapisane zmiany w rysunku — kopia powstanie z OSTATNIO ZAPISANEGO rysunku, a niezapisane zmiany przepadną. Kontynuować?",
                )
              )
                return;
              const n = window.prompt("Nazwa kopii wariantu:", `${active.name} (kopia)`);
              if (n?.trim()) onDuplicate(active.id, n.trim());
            }}
            className="p-1 text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-primary)] disabled:opacity-50"
          >
            <Copy size={14} />
          </button>
        </>
      )}
    </div>
  );
}
